<script setup lang="ts">
import {inject, ref} from 'vue'
import {useLocalization} from '@hantera/portal-app'
import type {useRoleStore} from '@/stores/iam/role-store'
import {ElasticInput} from '@hantera/design-system'

const store = inject<ReturnType<typeof useRoleStore>>('roleStore')!
const t = useLocalization('iam.roles')

const editing = ref(false)

function startEdit() {
  if (!store.canEdit) return
  editing.value = true
}

function cancelEdit() {
  store.discardDescription()
  editing.value = false
}

async function saveEdit() {
  await store.saveDescription()
  editing.value = false
}
</script>

<template>
  <div v-if="store.role" class="role-sidebar">

    <!-- System Role Info -->
    <div v-if="store.isSystemRole" class="box-warning p">
      {{ t('systemRoleInfo') }}
    </div>

    

    <!-- Description Section -->

    <fieldset v-if="editing">
      <dl>
        <dt><label for="description">{{ t('description') }}</label></dt>
        <dd>
          <ElasticInput v-model="store.displayDescription" :placeholder="t('descriptionPlaceholder')" />
        </dd>
      </dl>
    </fieldset>
    <dl v-else class="box-content">
      <dt>{{ t('description') }}</dt>
      <dd v-if="store.role.description" class="description-text">{{ store.role.description }}</dd>
      <dd v-else class="empty-text">{{ t('noDescription') }}</dd>
    </dl>

    

    <!-- Metadata -->
    <dl class="box-content" v-if="!editing">
      <dt>{{ t('created') }}</dt>
      <dd>{{ new Date(store.role.createdAt).toLocaleDateString() }}</dd>
      <template v-if="store.role.lastModifiedAt">
        <dt>{{ t('lastModified') }}</dt>
        <dd>{{ new Date(store.role.lastModifiedAt).toLocaleDateString() }}</dd>
      </template>
    </dl>

    <!-- Edit Controls -->
    <div v-if="store.canEdit" class="sidebar-actions">
      <template v-if="!editing">
        <button @click="startEdit" class="btn-secondary">
          {{ t('editDescription') }}
        </button>
        <button @click="store.deleteRole" :disabled="!store.canDeleteRole" class="btn-danger btn-small">
          {{ t('deleteRole') }}
        </button>
        <p v-if="(store.role.memberCount?.toNumber() ?? 0) > 0" class="error-text">
          {{ t('cannotDeleteWithMembers', { count: store.role.memberCount }) }}
        </p>
      </template>
      <template v-else>
        <button @click="saveEdit" class="btn-primary" :disabled="!store.hasUnsavedDescription">
          {{ t('saveChanges') }}
        </button>
        <button @click="cancelEdit" class="btn-silent">{{ t('cancel') }}</button>
      </template>
      
    </div>
  </div>

  <div v-else-if="store.isLoading" class="loading-state">
    <p>{{ t('loading') }}</p>
  </div>

  <div v-else class="empty-state">
    <p>{{ t('roleNotFound') }}</p>
  </div>
</template>

<style scoped>
.role-sidebar {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.role-name h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  word-break: break-word;
}

.sidebar-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
}

.empty-text {
  font-style: italic;
  margin: 0;
}

.danger-zone h4 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
}

.danger-zone p {
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
}

.error-text {
  font-size: 13px;
  margin: 0;
}

.btn-small {
  padding: 6px 12px;
  font-size: 13px;
}

.loading-state,
.empty-state {
  padding: 20px;
  text-align: center;
}
</style>
